import { trackEvent } from "@/app/_module/components/PostHogProvider";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

const STEP_NUMBERS: Record<string, number> = {
  page_visited: 1,
  account_completed: 2,
  plan_selected: 3,
  agency_completed: 4,
  area_code_selected: 5,
  submit_clicked: 6,
  registration_success: 7,
  registration_failed: 7,
  carrier_selected: 8,
  mfa_totp_verified: 9,
  mfa_sms_verified: 10,
  mfa_completed: 11,
  email_verification_sent: 12,
  approval_pending: 13,
  trial_abuse_flagged: 14,
};

// Clean display names for PostHog event naming
const STEP_EVENT_NAMES: Record<string, string> = {
  page_visited: "Signup Started",
  account_completed: "Signup Account Created",
  plan_selected: "Signup Plan Selected",
  agency_completed: "Signup Agency Setup",
  area_code_selected: "Signup Area Code Selected",
  submit_clicked: "Signup Submitted",
  registration_success: "Signup Completed",
  registration_failed: "Signup Failed",
  carrier_selected: "Signup Carrier Selected",
  mfa_totp_verified: "Signup MFA Authenticator Verified",
  mfa_sms_verified: "Signup MFA SMS Verified",
  mfa_completed: "Signup MFA Complete",
  email_verification_sent: "Signup Email Verification Sent",
  approval_pending: "Signup Awaiting Approval",
  trial_abuse_flagged: "Signup Trial Abuse Flagged",
};

const SESSION_KEY = "signup_funnel_session_id";

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "ssr";
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function getSignupSessionId(): string {
  if (typeof window === "undefined") return "";
  return sessionStorage.getItem(SESSION_KEY) || "";
}

const stepEntryTimestamps: Record<string, number> = {};

function getStepDuration(fromStep?: string): number | undefined {
  if (!fromStep || !stepEntryTimestamps[fromStep]) return undefined;
  return Date.now() - stepEntryTimestamps[fromStep];
}

function recordStepEntry(step: string): void {
  stepEntryTimestamps[step] = Date.now();
}

/**
 * Normalize a phone to E.164 (+<countrycode><number>) for TikTok Advanced
 * Matching. TikTok REJECTS malformed phones (must be E.164, no spaces/dashes)
 * and placeholder values ("undefined", "123-456-7890"). Returns undefined for
 * anything we can't confidently normalize so we never send TikTok a bad phone.
 *
 * Tracking-only + fire-and-forget: this only affects the TikTok identify
 * payload — it cannot touch the signup form, validation, or submission.
 */
function normalizePhoneE164(raw?: string): string | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "undefined" || s.toLowerCase() === "null") return undefined;
  const digits = s.replace(/\D/g, "");
  if (!digits) return undefined;
  // Reject obvious placeholders/test numbers (TikTok flags these explicitly).
  const core = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (/^(\d)\1+$/.test(core) || core === "1234567890") return undefined;
  if (s.startsWith("+")) return digits.length >= 8 && digits.length <= 15 ? "+" + digits : undefined;
  if (digits.length === 10) return "+1" + digits;                  // US/CA, no country code
  if (digits.length === 11 && digits.startsWith("1")) return "+" + digits;
  if (digits.length >= 11 && digits.length <= 15) return "+" + digits; // international
  return undefined;
}

/** Trimmed, lowercased email — or undefined if missing/garbage. */
function cleanEmail(raw?: string): string | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim().toLowerCase();
  if (!s || s === "undefined" || s === "null" || !s.includes("@")) return undefined;
  return s;
}

async function hashEmail(email: string): Promise<string> {
  if (!email) return "";
  const encoder = new TextEncoder();
  const data = encoder.encode(email.toLowerCase().trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .substring(0, 16);
}

export interface FunnelStepOptions {
  email?: string;
  phone?: string;
  npn?: string;
  selectedPlan?: string;
  errorMessage?: string;
  stepData?: Record<string, any>;
  previousStep?: string;
}

/**
 * Log a signup funnel step to PostHog, Meta Pixel, TikTok Pixel, and the
 * backend database. Fire-and-forget: never blocks the UI.
 */
export function logSignupStep(
  step: string,
  options: FunnelStepOptions = {}
): void {
  const sessionId = getOrCreateSessionId();
  const stepNumber = STEP_NUMBERS[step] || 0;
  const durationOnPreviousStepMs = getStepDuration(options.previousStep);

  recordStepEntry(step);

  // 1. PostHog event
  (async () => {
    try {
      const emailHash = await hashEmail(options.email || "");
      trackEvent(STEP_EVENT_NAMES[step] || `Signup ${step}`, {
        step,
        step_number: stepNumber,
        email_hash: emailHash,
        npn_last4: options.npn ? options.npn.slice(-4) : undefined,
        selected_plan: options.selectedPlan,
        time_on_previous_step_ms: durationOnPreviousStepMs,
        error_message: options.errorMessage,
        session_id: sessionId,
        ...options.stepData,
      });
    } catch {
      // Never block UI
    }
  })();

  // 2. Meta Pixel event (fire-and-forget, never blocks UI)
  try {
    if (typeof window !== "undefined" && typeof (window as any).fbq === "function") {
      const fbq = (window as any).fbq;
      if (step === "registration_success") {
        fbq("track", "CompleteRegistration", {
          content_name: options.selectedPlan || "unknown",
        });
      } else if (step === "registration_failed") {
        fbq("trackCustom", "SignupFailed", {
          step_number: stepNumber,
          error: options.errorMessage || "unknown",
        });
      } else {
        fbq("trackCustom", "SignupStep", {
          step,
          step_number: stepNumber,
          selected_plan: options.selectedPlan,
        });
      }
    }
  } catch {
    // Never block UI
  }

  // 3. TikTok Pixel events (fire-and-forget, never blocks UI).
  // Maps our signup funnel onto TikTok's standard commerce-funnel events so
  // delivery optimization has the events the TikTok diagnostic flagged as
  // missing (ViewContent / AddToCart / InitiateCheckout / Purchase). Base
  // pixel (ttq.load + ttq.page) is in app/layout.tsx; here we only fire the
  // funnel conversions. event_id is set so a future server-side Events API
  // call can be DEDUPED against these pixel events.
  try {
    if (typeof window !== "undefined" && typeof (window as any).ttq?.track === "function") {
      const ttq = (window as any).ttq;

      // Advanced Matching — only send VALID, normalized identifiers. TikTok's
      // pixel SHA-256 hashes them client-side, but rejects a non-E.164 phone or
      // placeholder ("undefined"/"123-456-7890"), so we normalize first and omit
      // anything we can't clean. Never sends an empty/garbage value.
      const ttEmail = cleanEmail(options.email);
      const ttPhone = normalizePhoneE164(options.phone);
      if (ttEmail || ttPhone) {
        try {
          const identifyPayload: Record<string, string> = {};
          if (ttEmail) identifyPayload.email = ttEmail;
          if (ttPhone) identifyPayload.phone_number = ttPhone;
          ttq.identify(identifyPayload);
        } catch {
          // ignore identify failures
        }
      }

      const contents = {
        content_type: "product",
        content_id: options.selectedPlan || "insuracentral_plan",
        content_name: options.selectedPlan || "InsuraCentral",
      };

      if (step === "page_visited") {
        ttq.track("ViewContent", contents, { event_id: `${sessionId}:viewcontent` });
      } else if (step === "plan_selected") {
        ttq.track("AddToCart", contents, { event_id: `${sessionId}:addtocart` });
      } else if (step === "submit_clicked") {
        ttq.track("InitiateCheckout", contents, { event_id: `${sessionId}:checkout` });
      } else if (step === "registration_success") {
        // Signup completed = a registration conversion, NOT a purchase (no money
        // changes hands at free-trial signup). The real "Purchase" event fires
        // on paid subscription checkout — see SubscriptionPlans.tsx.
        ttq.track("CompleteRegistration", contents, { event_id: `${sessionId}:registration` });
      }
    }
  } catch {
    // Never block UI
  }

  // 4. Backend API call
  try {
    api
      .post({
        url: getApiUrl(endpoint.auth.postSignupFunnelLog),
        body: {
          sessionId,
          email: options.email,
          phone: options.phone,
          step,
          stepNumber,
          stepData: {
            selectedPlan: options.selectedPlan,
            npnLast4: options.npn ? options.npn.slice(-4) : undefined,
            ...options.stepData,
          },
          durationOnPreviousStepMs,
          errorMessage: options.errorMessage,
          referrer: typeof document !== "undefined" ? document.referrer : undefined,
        },
        auth: false,
      })
      .catch(() => {});
  } catch {
    // Never block UI
  }
}

export function clearFunnelSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Ignore
  }
}
